import React, { useState, useCallback } from 'react';
import { AutoComplete, Input} from 'antd';
import addressService from '../../../services/AddressService';

const StreetAutocomplete = ({ 
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Zadajte ulicu a číslo',
  disabled = false,
  googleApiKey = "AIzaSyAUiCHKFy9HYU6ZoywgvfA75vnxRP6FVKI"
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationStarted, setInitializationStarted] = useState(false);

  const initializeApiIfNeeded = useCallback(async () => {
    if (initializationStarted || isInitialized || !googleApiKey) {
      return;
    }

    setInitializationStarted(true);
    
    try {
      await addressService.initializeGoogleMaps(googleApiKey);
      // Additional check to ensure services are properly initialized
      if (addressService.autocompleteService && addressService.placesService)
        setIsInitialized(true);
      else
        throw new Error('Services not properly initialized');

    } catch (error) {
      setIsInitialized(false);
      setInitializationStarted(false);
    }
  }, [googleApiKey, initializationStarted, isInitialized]);

  // Debounced search function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Search for address predictions
  const searchAddresses = useCallback(
    debounce(async (searchText) => {
      if (!isInitialized || !searchText || searchText.length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const predictions = await addressService.getAddressPredictions(searchText);
        const formattedOptions = predictions.map((prediction, index) => ({
          value: prediction.placeId, // Use placeId as value to ensure uniqueness
          key: prediction.placeId || `prediction-${index}`,
          label: (
            <div>
              <div style={{ fontWeight: 500 }}>{prediction.mainText}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {prediction.secondaryText}
              </div>
            </div>
          ),
          placeId: prediction.placeId,
          fullDescription: prediction.description,
          mainText: prediction.mainText,
          secondaryText: prediction.secondaryText
        }));
        setOptions(formattedOptions);
      } catch (error) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [isInitialized]
  );

  // Handle search input change
  const handleSearch = async (searchValue) => {
    if (searchValue && searchValue.length >= 2 && !isInitialized && !initializationStarted) {
      await initializeApiIfNeeded();
    }
    searchAddresses(searchValue);
  };

  // Handle input change
  const handleChange = (searchValue) => {
    onChange(searchValue);
  };

  // Handle address selection
  const handleSelect = async (selectedValue, option) => {
    if (!option || !option.placeId) return;

    // Show loading text in input immediately
    onChange('Načítavam...');
    setLoading(true);
    
    try {
      const placeDetails = await addressService.getPlaceDetails(option.placeId);
      
      // Update the input with street and orient number
      const streetValue = placeDetails.formattedAddress.split(",")[0] || (placeDetails.fullStreet + " " + placeDetails.streetNumber).trim() || option.mainText;

      onChange(streetValue);
      
      // Call callback function with full address details
      if (onAddressSelect) {
        onAddressSelect({
          street: streetValue,
          city: placeDetails.city || '',
          postalCode: placeDetails.postalCode || '',
          country: placeDetails.country || '',
          region: placeDetails.region || '',
          coordinates: placeDetails.coordinates,
          formattedAddress: placeDetails.formattedAddress,
          placeId: option.placeId
        });
      }

      // Create new session token for next search
      addressService.createNewSessionToken();
      
    } catch (error) {
      // Restore original text on error
      onChange(option.mainText);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AutoComplete
      value={value}
      options={options}
      onSearch={handleSearch}
      onChange={handleChange}
      onSelect={handleSelect}
      disabled={disabled}
      className="google-address-autocomplete"
      loading={loading}
      style={{ width: '100%' }}
      filterOption={false}
      notFoundContent={loading ? 'Vyhľadávam...' : null}
    >
      <Input
        placeholder={placeholder}
        disabled={disabled}
      />
    </AutoComplete>
  );
};

export default StreetAutocomplete;
